import { useState, type DragEvent, type ReactNode } from 'react';
import { Icon } from './Icon.js';

/**
 * Choosing some of a list, as two lists side by side (owner, 29 September 2026: "2 tables the one
 * on the left has the agents in the queue the one on the right has the ones available ... drag and
 * drop to remove or add"). Drag a row across to add or remove it, or drag within the chosen list to
 * reorder it. Every drag has a button that does the same, so nothing needs a mouse.
 */
export interface PickItem { id: string; label: ReactNode; code?: string; detail?: ReactNode; search: string }

export function DragPicker({ items, chosen, onChange, chosenTitle, availableTitle, ordered = false, empty,
  chosenNote, availableTools, availableFilter, chosenFoot }: {
  items: PickItem[];
  /** The chosen ids, in order. */
  chosen: string[];
  onChange: (next: string[]) => void;
  chosenTitle: string;
  availableTitle: string;
  /** Whether the chosen list's order means something (it can then be reordered). */
  ordered?: boolean;
  /** Said in the chosen list when it is empty. */
  empty?: string;
  /** A few words beside the chosen list's title, e.g. how it rings. */
  chosenNote?: ReactNode;
  /** More ways to narrow the available list (a department select), beside its search. */
  availableTools?: ReactNode;
  availableFilter?: (item: PickItem) => boolean;
  /** Under the chosen list: another way to add to it (an outside number). */
  chosenFoot?: ReactNode;
}) {
  const [q, setQ] = useState('');
  const [over, setOver] = useState<'chosen' | 'available' | null>(null);
  const [overRow, setOverRow] = useState<number | null>(null);
  const byId = new Map(items.map((i) => [i.id, i]));
  const inList = new Set(chosen);
  const needle = q.trim().toLowerCase();
  const available = items.filter((i) => !inList.has(i.id) && (!availableFilter || availableFilter(i))
    && (needle === '' || i.search.toLowerCase().includes(needle)));

  const add = (id: string, at = chosen.length) => {
    const rest = chosen.filter((x) => x !== id);
    onChange([...rest.slice(0, at), id, ...rest.slice(at)]);
  };
  const remove = (id: string) => onChange(chosen.filter((x) => x !== id));
  const move = (i: number, by: -1 | 1) => {
    const j = i + by;
    if (j < 0 || j >= chosen.length) return;
    const next = [...chosen];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  const drag = (id: string) => (e: DragEvent) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const dropOn = (side: 'chosen' | 'available', at?: number) => (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const id = e.dataTransfer.getData('text/plain');
    setOver(null); setOverRow(null);
    if (!byId.has(id)) return;
    if (side === 'available') remove(id);
    else add(id, at ?? chosen.length);
  };
  const allow = (side: 'chosen' | 'available') => (e: DragEvent) => { e.preventDefault(); setOver(side); };

  return (
    <div className="arc-dragpick">
      <div className={`arc-pick-pane${over === 'chosen' ? ' is-over' : ''}`}
        onDragOver={allow('chosen')} onDragLeave={() => setOver(null)} onDrop={dropOn('chosen')}>
        <div className="arc-pick-head">
          <strong style={{ fontSize: 12.5 }}>{chosenTitle}</strong>
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>{chosen.length}</span>
          {chosenNote && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{chosenNote}</span>}
          <div className="arc-spacer" />
          {chosen.length > 0 && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => onChange([])}>Remove all</button>
          )}
        </div>
        <div className="arc-pick-list">
          {chosen.length === 0 ? (
            <p className="arc-dragpick-empty">{empty ?? 'Nobody yet.'} Drag from the right, or use Add.</p>
          ) : chosen.map((id, i) => {
            const it = byId.get(id);
            return (
              <div key={id} className={`arc-pick-row arc-dragpick-row${overRow === i ? ' is-drop' : ''}`} draggable
                onDragStart={drag(id)} onDragOver={(e) => { e.preventDefault(); setOver('chosen'); setOverRow(i); }}
                onDrop={dropOn('chosen', i)}>
                <Icon name="grip" size={13} />
                {ordered && <span className="arc-pick-pos">{i + 1}</span>}
                {it?.code && <span className="arc-mono">{it.code}</span>}
                <span className="arc-truncate" style={{ flex: 1 }}>{it?.label ?? <span className="arc-subtle">no longer exists</span>}</span>
                {it?.detail && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{it.detail}</span>}
                {ordered && (
                  <>
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-label={`Move ${it?.code ?? ''} up`}
                      disabled={i === 0} onClick={() => move(i, -1)}><Icon name="chevronUp" size={12} /></button>
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-label={`Move ${it?.code ?? ''} down`}
                      disabled={i === chosen.length - 1} onClick={() => move(i, 1)}><Icon name="chevronDown" size={12} /></button>
                  </>
                )}
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label={`Remove ${it?.code ?? ''}`}
                  onClick={() => remove(id)}>Remove <Icon name="chevronRight" size={12} /></button>
              </div>
            );
          })}
        </div>
        {chosenFoot && <div className="arc-pick-foot">{chosenFoot}</div>}
      </div>

      <div className={`arc-pick-pane${over === 'available' ? ' is-over' : ''}`}
        onDragOver={allow('available')} onDragLeave={() => setOver(null)} onDrop={dropOn('available')}>
        <div className="arc-pick-head">
          <strong style={{ fontSize: 12.5 }}>{availableTitle}</strong>
          <div className="arc-search" style={{ flex: '1 1 140px' }}>
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search" aria-label={`Search ${availableTitle.toLowerCase()}`}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {availableTools}
          {available.length > 1 && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => onChange([...chosen, ...available.map((i) => i.id)])}>Add all {available.length}</button>
          )}
        </div>
        <div className="arc-pick-list">
          {available.length === 0 ? (
            <p className="arc-dragpick-empty">{items.every((i) => inList.has(i.id)) ? 'Everyone is in.' : 'Nobody matches.'}</p>
          ) : available.map((it) => (
            <div key={it.id} className="arc-pick-row arc-dragpick-row" draggable onDragStart={drag(it.id)}>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-label={`Add ${it.code ?? ''}`}
                onClick={() => add(it.id)}><Icon name="chevronLeft" size={12} /> Add</button>
              {it.code && <span className="arc-mono">{it.code}</span>}
              <span className="arc-truncate" style={{ flex: 1 }}>{it.label}</span>
              {it.detail && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{it.detail}</span>}
              <Icon name="grip" size={13} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
